import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDilemmasFormStore } from "../../stores/useDilemmaFormStore";

interface DilemmaFormStepIndicatorProps {
  step: number;
  title: string;
  isDirty?: boolean;
}

const DilemmaFormStepIndicator = ({
  step,
  title,
  isDirty,
}: DilemmaFormStepIndicatorProps) => {
  const { t } = useTranslation();
  const { currentStep, isEditMode, setCurrentStep } = useDilemmasFormStore();

  const isPassed = currentStep > step;
  const isCurrent = currentStep === step;
  const showDirty = isDirty && !isCurrent;

  const canNavigate = isEditMode && step < currentStep;

  function handleClick() {
    if (canNavigate) {
      setCurrentStep(step);
    }
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        "flex items-center justify-center lg:justify-start flex-col lg:flex-row gap-3 flex-1 text-center lg:text-left",
        canNavigate && "cursor-pointer",
      )}
    >
      <div className="relative shrink-0">
        <div
          className={cn(
            "size-6 text-xs rounded-full flex items-center justify-center border",
            isPassed && "border-green-500 bg-green-100 text-green-600",
            isCurrent && "bg-[#3745AF] border-[#3745AF] text-white",
            !isPassed && !isCurrent && "bg-white border-gray-300 text-gray-400",
          )}
        >
          {isPassed ? (
            <Check className="size-3" strokeWidth={3} />
          ) : (
            <span className="translate-y-px">{step}</span>
          )}
        </div>
        {showDirty && (
          <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-orange-500" />
        )}
      </div>
      <p
        className={cn(
          "text-xs lg:text-sm w-full h-12 lg:h-auto max-w-32.5 lg:max-w-max text-balance",
          isCurrent ? "text-gray-900 font-medium" : "text-gray-500",
        )}
      >
        {title}
        {showDirty && (
          <span className="hidden lg:block text-xs font-medium text-orange-500">
            {t("keys.modified")}
          </span>
        )}
      </p>
    </div>
  );
};

export default DilemmaFormStepIndicator;
